import React, { useEffect, useState } from 'react';
import { Modal, Radio, message } from 'antd';
import s from './s.module.less';
import { growthSettingSave } from 'api/common';
import { getUserId } from 'utils/localstore';
import useAdStatus from 'hooks/marketing/useAdStatus';
import { TGrowthTierSetting, EGrowthTierSetting } from 'types/common';
import cx from 'classnames';

type Props = {
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

export default function GrowthGoalModal({ open, onCancel, onConfirm }: Props) {
    const [selectedOption, setSelectedOption] = useState<TGrowthTierSetting | null>(null);
    const [loading, setLoading] = useState(false);
    const providerId = getUserId();
     const [adStatus] = useAdStatus();

    // Check if provider is enrolled in Pro-Listings
    const isProListingsEnrolled = adStatus.hasJoinedAdSystem;

    useEffect(() => {
        if (open) {
            setSelectedOption(null);
        }
    }, [open]);

    const handleOptionSelect = async (value: TGrowthTierSetting) => {
        if (!value) return;

        setLoading(true);
        try {
            // If user selects SCALING but is not enrolled in Pro-Listings, default to GROWTH
            let growthTier: TGrowthTierSetting = value;
            if (value === EGrowthTierSetting.SCALING && !isProListingsEnrolled) {
                growthTier = EGrowthTierSetting.GROWTH;
            }

            const res = await growthSettingSave(growthTier);
            setLoading(false);

            if (!res.error) {
                message.success('Growth goal saved successfully');
                onConfirm();
            } else {
                message.error(res.error || 'Failed to save growth goal');
            }
        } catch (error) {
            setLoading(false);
            message.error('An error occurred while saving your growth goal');
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            title={null}
            footer={null}
            width={788}
            centered
            className={s.modal}
            destroyOnClose
        >
            <div className={s.content}>
                <div className={s.header}>
                    <h2 className={s.title}>
                        Tell us about your patient growth goal
                    </h2>
                    <p className={s.subtitle}>
                        Help us customize your patient acquisition speed.
                    </p>
                </div>

                <Radio.Group
                    value={selectedOption}
                    onChange={async (e) => {
                        const { value } = e.target;
                        setSelectedOption(value);
                        // 选择后自动确认
                        await handleOptionSelect(value);
                    }}
                    className={s.radioGroup}
                    disabled={loading}
                >
                    {/* Option 1: Steady Start */}
                    <Radio
                        value={EGrowthTierSetting.STARTER}
                        className={`${s.radioOption} ${
                            selectedOption === EGrowthTierSetting.STARTER
                                ? s.selected
                                : ''
                        } ${loading ? s.loading : ''}`}
                        disabled={loading}
                    >
                        <div className={s.optionContent}>
                            <div className={cx(s.optionIcon, s['optionIcon-1'])}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        d="M3.33464 1.66699V16.667H5.0013V7.50032H10.0013V16.667H11.668V4.16699H16.668V16.667H18.3346V18.3337H1.66797V1.66699H3.33464ZM15.0013 16.667V5.83366H13.3346V16.667H15.0013ZM8.33463 16.667V9.16699H6.66797V16.667H8.33463Z"
                                        fill="#92400E"
                                    />
                                </svg>
                            </div>
                            <div className={s.optionText}>
                                <div className={s.optionHeader}>
                                    <div className={s.optionTitleRow}>
                                        <span className={s.optionTitle}>
                                            I want a steady start
                                        </span>
                                        <span className={s.badgeGreen}>
                                            You&apos;re eligible already
                                        </span>
                                    </div>
                                    <p className={s.optionSubtitle}>
                                        1-5 bookings per week
                                    </p>
                                </div>
                                <p className={s.optionDescription}>
                                    You&apos;re already eligible for this
                                    program. You&apos;re just getting started
                                    and want to a manageable pace. You&apos;ll
                                    likely be shown along with other providers
                                    in a provider listing page.
                                </p>
                            </div>
                        </div>
                    </Radio>

                    {/* Option 2: Grow Practice */}
                    <Radio
                        value={EGrowthTierSetting.GROWTH}
                        className={`${s.radioOption} ${
                            selectedOption === EGrowthTierSetting.GROWTH
                                ? s.selected
                                : ''
                        } ${loading ? s.loading : ''}`}
                        disabled={loading}
                    >
                        <div className={s.optionContent}>
                            <div className={cx(s.optionIcon, s['optionIcon-2'])}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        d="M4.375 3.4375C4.375 3.18886 4.27623 2.9504 4.10041 2.77459C3.9246 2.59877 3.68614 2.5 3.4375 2.5C3.18886 2.5 2.9504 2.59877 2.77459 2.77459C2.59877 2.9504 2.5 3.18886 2.5 3.4375V14.0625C2.5 14.9742 2.86216 15.8485 3.50682 16.4932C4.15148 17.1378 5.02582 17.5 5.9375 17.5H16.5625C16.8111 17.5 17.0496 17.4012 17.2254 17.2254C17.4012 17.0496 17.5 16.8111 17.5 16.5625C17.5 16.3139 17.4012 16.0754 17.2254 15.8996C17.0496 15.7238 16.8111 15.625 16.5625 15.625H5.9375C5.075 15.625 4.375 14.925 4.375 14.0625V3.4375ZM12.1875 5.9375C12.1875 6.455 12.6075 6.875 13.125 6.875H14.3L11.25 9.925L9.4125 8.0875C9.23672 7.91194 8.99844 7.81332 8.75 7.81332C8.50156 7.81332 8.26328 7.91194 8.0875 8.0875L6.2125 9.9625C6.0469 10.1402 5.95675 10.3753 5.96103 10.6182C5.96532 10.861 6.06371 11.0928 6.23547 11.2645C6.40724 11.4363 6.63897 11.5347 6.88185 11.539C7.12472 11.5433 7.35978 11.4531 7.5375 11.2875L8.75 10.075L10.5875 11.9125C10.7633 12.0881 11.0016 12.1867 11.25 12.1867C11.4984 12.1867 11.7367 12.0881 11.9125 11.9125L15.625 8.2V9.47125C15.625 9.71989 15.7238 9.95835 15.8996 10.1342C16.0754 10.31 16.3139 10.4087 16.5625 10.4087C16.8111 10.4087 17.0496 10.31 17.2254 10.1342C17.4012 9.95835 17.5 9.71989 17.5 9.47125V5.9375C17.5 5.68886 17.4012 5.4504 17.2254 5.27459C17.0496 5.09877 16.8111 5 16.5625 5H13.125C12.8764 5 12.6379 5.09877 12.4621 5.27459C12.2863 5.4504 12.1875 5.68886 12.1875 5.9375Z"
                                        fill="#00816B"
                                    />
                                </svg>
                            </div>
                            <div className={s.optionText}>
                                <div className={s.optionHeader}>
                                    <div className={s.optionTitleRow}>
                                        <span className={s.optionTitle}>
                                            I want to grow my practice
                                        </span>
                                        <span className={s.badgeGreen}>
                                            You&apos;re eligible already
                                        </span>
                                    </div>
                                    <p className={s.optionSubtitle}>
                                        5–15 bookings per week
                                    </p>
                                </div>
                                <p className={s.optionDescription}>
                                    You&apos;re already eligible for this
                                    program. You&apos;re ready to grow your case
                                    load and gain more visibility online.
                                    We&apos;ll display your profile in several
                                    high-traffic pages.
                                </p>
                            </div>
                        </div>
                    </Radio>

                    {/* Option 3: Scale Fast */}
                    <Radio
                        value={EGrowthTierSetting.SCALING}
                        className={`${s.radioOption} ${
                            selectedOption === EGrowthTierSetting.SCALING
                                ? s.selected
                                : ''
                        } ${loading ? s.loading : ''}`}
                        disabled={loading}
                    >
                        <div className={s.optionContent}>
                            <div className={cx(s.optionIcon, s['optionIcon-3'])}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        d="M15.3501 2.3311L15.6718 2.37776C16.1718 2.45693 16.7318 2.58943 17.0759 2.9336C17.4201 3.27776 17.5534 3.8386 17.6326 4.3386L17.6793 4.65943C17.7843 5.41776 17.8118 6.41193 17.6326 7.51526C17.2793 9.69276 16.1201 12.2903 13.2093 14.3028C13.1965 14.4516 13.1918 14.601 13.1951 14.7503L13.2093 15.2028C13.2176 15.5044 13.2126 15.8053 13.1293 16.0944C12.9709 16.6444 12.4068 17.0069 11.8793 17.2669L11.6209 17.3894L11.2876 17.5361L11.0601 17.6278C10.4709 17.8528 9.76009 18.0103 9.29509 17.5444C9.08425 17.3336 8.98675 17.0469 8.90842 16.7603L8.87009 16.6169C8.73675 16.1111 8.58592 15.6086 8.39759 15.1219C8.34559 15.1852 8.29053 15.2458 8.23259 15.3036C7.77842 15.7569 7.16259 15.9853 6.54675 16.1619L6.13759 16.2744L5.72925 16.3828L5.33092 16.4828L4.97092 16.5661L4.54009 16.6578L4.27092 16.7103C4.13668 16.7351 3.9984 16.7269 3.86802 16.6865C3.73765 16.646 3.61907 16.5744 3.52254 16.4778C3.426 16.3813 3.3544 16.2627 3.3139 16.1323C3.2734 16.0019 3.26523 15.8637 3.29009 15.7294L3.38259 15.2703L3.49009 14.7886L3.59342 14.3636L3.78092 13.6594C3.96675 12.9761 4.19259 12.2719 4.69759 11.7678L4.78009 11.6886L4.14425 11.4603L3.50509 11.2403C3.13092 11.1169 2.73759 10.9869 2.46592 10.7153C1.96925 10.2186 2.18009 9.4436 2.42759 8.83443L2.56592 8.51193L2.68009 8.2611C2.94425 7.69943 3.31925 7.05276 3.91592 6.8811C4.20509 6.79776 4.50509 6.79276 4.80759 6.80026L5.26009 6.81526C5.41009 6.81915 5.55925 6.81443 5.70759 6.8011C7.71925 3.89026 10.3176 2.7311 12.4951 2.37776C13.4393 2.22218 14.4013 2.20646 15.3501 2.3311ZM12.7618 4.02276C10.9301 4.32026 8.70425 5.3011 6.96759 7.9136C6.75675 8.23026 6.40092 8.3811 6.03842 8.4386C5.83342 8.47193 5.62675 8.48026 5.41925 8.48026L4.79675 8.4711C4.69342 8.47054 4.58981 8.47221 4.48592 8.4761C4.23259 8.81276 4.05592 9.21276 3.92342 9.61026L5.17342 10.0644L5.68759 10.2586C6.62759 10.6269 7.53759 11.0728 8.23759 11.7728C9.41675 12.9519 10.0159 14.4944 10.4493 16.0703C10.8293 15.9369 11.2109 15.7669 11.5343 15.5244L11.5393 15.2136L11.5301 14.5919C11.5301 14.3853 11.5384 14.1786 11.5718 13.9719C11.6284 13.6094 11.7801 13.2536 12.0968 13.0428C14.7093 11.3061 15.6901 9.08026 15.9876 7.2486C16.1376 6.3236 16.1126 5.49693 16.0284 4.88776C15.996 4.63505 15.9422 4.38554 15.8676 4.14193C15.624 4.06758 15.3744 4.01406 15.1218 3.98193C14.3373 3.88027 13.5421 3.89348 12.7618 4.02276ZM5.87509 12.9461C5.58342 13.2986 5.44759 13.7611 5.33842 14.2194L5.24842 14.6094L5.20259 14.7978L5.78092 14.6619C6.23925 14.5536 6.70259 14.4161 7.05425 14.1244C7.21051 13.9681 7.29825 13.756 7.29817 13.535C7.29809 13.3139 7.2102 13.1019 7.05384 12.9457C6.89747 12.7894 6.68543 12.7017 6.46438 12.7018C6.24332 12.7018 6.03134 12.7897 5.87509 12.9461ZM10.5893 7.05443C10.744 6.89963 10.9277 6.77683 11.1299 6.69304C11.3321 6.60924 11.5488 6.56609 11.7677 6.56605C11.9866 6.56602 12.2033 6.60909 12.4056 6.69281C12.6078 6.77654 12.7915 6.89927 12.9463 7.05401C13.1011 7.20876 13.2239 7.39247 13.3077 7.59467C13.3915 7.79687 13.4347 8.01359 13.4347 8.23247C13.4348 8.45134 13.3917 8.66808 13.308 8.87031C13.2242 9.07254 13.1015 9.2563 12.9468 9.4111C12.6341 9.72372 12.2101 9.89935 11.768 9.89935C11.3259 9.89935 10.9019 9.72372 10.5893 9.4111C10.2766 9.09847 10.101 8.67446 10.101 8.23235C10.101 7.79023 10.2766 7.36622 10.5893 7.0536V7.05443Z"
                                        fill="#1E40AF"
                                    />
                                </svg>
                            </div>
                            <div className={s.optionText}>
                                <div className={s.optionHeader}>
                                    <div className={s.optionTitleRow}>
                                        <span className={s.optionTitle}>
                                            I want to scale fast
                                        </span>
                                        <span className={s.badgeBlue}>
                                            🎁$150 free credit available
                                        </span>
                                    </div>
                                    <p className={s.optionSubtitle}>
                                        More than 15 bookings per week
                                    </p>
                                </div>
                                <p className={s.optionDescription}>
                                    You&apos;ll be put in specially curated
                                    workflows and premium locations to gain
                                    patient visibility.
                                </p>
                            </div>
                        </div>
                    </Radio>
                </Radio.Group>
            </div>
        </Modal>
    );
}
